import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import { MOD_GROUP_ROW_GUTTER_PX } from './ModGroupRow';

export const DRAWER_BACKGROUND = '#EFF6FB';

/**
 * Only the OPTION LIST columnises. Groups never do — a group row is a full-width
 * line at every pane width, which is the whole point of the row rhythm.
 *
 * Container queries would be the honest tool, but the pane is resized by
 * `react-resizable-panels` rather than by the viewport, so `auto-fill` against a
 * minimum column width gets the same result without measuring: 2 columns once
 * there is room for two ~190px tracks, 3 once there is room for three.
 */
const MIN_COLUMN_PX = 186;

const useStyles = makeStyles()(() => ({
  drawer: {
    display: 'flex',
    backgroundColor: DRAWER_BACKGROUND,
    marginBottom: 4,
  },
  // The blue left rail. With the columnised list it already reads as a picker,
  // which is why the frozen design carries no verb chip: an unmet constraint is
  // said once, by the group row's own `! Choose N`.
  rail: {
    flexShrink: 0,
    width: 3,
    backgroundColor: Colors.readBack,
  },
  options: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_COLUMN_PX}px, 1fr))`,
    // HITL-1619: the same right gutter the group rows carry, so the drawer's
    // last column stops where the row above it does instead of running to the
    // card's edge.
    padding: `4px ${MOD_GROUP_ROW_GUTTER_PX}px 4px 6px`,
  },
}));

interface OptionDrawerProps {
  children: React.ReactNode;
  /** Indent depth of the group this drawer belongs to. */
  indentPx?: number;
}

/**
 * HITL-1616 — a group's own options, so a picker can never be misread as child
 * rows.
 */
const OptionDrawer = ({ children, indentPx = 0 }: OptionDrawerProps) => {
  const { classes } = useStyles();
  return (
    <div
      className={classes.drawer}
      style={indentPx ? { marginLeft: indentPx } : undefined}
      data-testid="option-drawer"
    >
      <div className={classes.rail} />
      <div className={classes.options}>{children}</div>
    </div>
  );
};

export default OptionDrawer;
